import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  CardGiftcard as GiftIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';
import { verifyInviteCode, resetInviteState } from '../../store/actions/inviteActions';

const InviteCodeModal = ({ open, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { verifying, verifySuccess, verifyError, verifyResult } = useSelector(state => state.invite);
  
  const [inviteCode, setInviteCode] = useState('');
  const [localError, setLocalError] = useState('');

  // 重置状态
  useEffect(() => {
    if (open) {
      setInviteCode('');
      setLocalError('');
      dispatch(resetInviteState());
    }
  }, [open, dispatch]);

  // 处理验证成功
  useEffect(() => {
    if (verifySuccess && verifyResult) {
      // 延迟关闭，让用户看到成功信息
      setTimeout(() => {
        onClose();
        if (onSuccess) {
          onSuccess(verifyResult);
        }
      }, 2000);
    }
  }, [verifySuccess, verifyResult, onClose, onSuccess]);

  // 验证邀请码格式
  const validateInviteCode = (code) => {
    if (!code || code.trim().length === 0) {
      return '请输入邀请码';
    }
    if (code.trim().length !== 12) {
      return '邀请码必须是12位字符';
    }
    return '';
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setInviteCode(value);
    setLocalError('');
  };

  // 处理提交
  const handleSubmit = async () => {
    const trimmedCode = inviteCode.trim();
    const validationError = validateInviteCode(trimmedCode);
    
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    try {
      await dispatch(verifyInviteCode(trimmedCode));
    } catch (error) {
      logger.error('验证邀请码失败:', error);
    }
  };

  // 处理关闭
  const handleClose = () => {
    if (!verifying) {
      onClose();
    }
  };

  // 处理键盘事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !verifying && inviteCode.trim()) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      {/* 标题栏 */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GiftIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              填写邀请码
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={verifying}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* 内容区域 */}
      <DialogContent sx={{ py: 3 }}>
        {verifySuccess ? (
          // 成功状态
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: 'success.main', fontWeight: 600 }}>
              邀请码使用成功！
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              恭喜您获得 {verifyResult?.benefits?.bonus_points || 2} 个点数奖励
            </Typography>
            <Alert severity="success" sx={{ mt: 2 }}>
              点数已自动充值到您的账户，感谢您的使用！
            </Alert>
          </Box>
        ) : (
          // 输入状态
          <>
            {/* 提示信息 */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>邀请码奖励：</strong>填写有效邀请码可获得 <strong>2</strong> 点数奖励
              </Typography>
            </Alert>

            {/* 输入框 */}
            <TextField
              fullWidth
              label="邀请码"
              value={inviteCode}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="请输入12位邀请码"
              disabled={verifying}
              error={!!(localError || verifyError)}
              helperText={localError || verifyError || '邀请码由12位数字和字母组成'}
              inputProps={{
                maxLength: 12,
                style: { 
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontFamily: 'monospace',
                  fontSize: '1.1em'
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: localError || verifyError ? 'error.main' : 'primary.main'
                  }
                }
              }}
            />

            {/* 使用须知 */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                使用须知：
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                • 每个账户只能使用一次邀请码
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                • 注册后24小时内可填邀请码
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • 验证成功后点数立即到账
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      {/* 操作按钮 */}
      {!verifySuccess && (
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleClose}
            disabled={verifying}
            sx={{ minWidth: 80 }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={verifying || !inviteCode.trim()}
            startIcon={verifying ? <CircularProgress size={16} /> : null}
            sx={{ minWidth: 100 }}
          >
            {verifying ? '验证中...' : '验证'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default InviteCodeModal; 